import React, { useState } from "react";
import clsx from "clsx";
import { DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import { Button, useSnackBar } from "@truevault/aquilo";

const GTM_SETUP_URL =
  "https://developers.truevault.com/docs/google-tag-manager/setting-up-the-consent-banner";

// Styled like the help card on the Vendor List page.
const HelpCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="rounded-xl border border-solid border-neutral-200 bg-white p-[24px] shadow-[0px_1px_2px_0px_#1018280D]">
    <h2 className="m-0 text-[16px] font-semibold leading-[24px] text-neutral-900">{title}</h2>
    {children}
  </section>
);

const HelpParagraph = ({ children }: { children: React.ReactNode }) => (
  <p className="mb-0 mt-[4px] max-w-[68ch] text-[14px] leading-[21px] text-neutral-600 [&_a]:font-medium [&_a]:text-primary-600 [&_a]:underline">
    {children}
  </p>
);

const HelpHeading = ({ children }: { children: React.ReactNode }) => (
  <h3 className="m-0 text-[14px] font-semibold leading-[21px] text-neutral-800">{children}</h3>
);

/** Production: what a consent banner is and how it works. */
export const ConsentBannerProductionHelpCard = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <HelpCard title="What is a consent banner?">
      <HelpParagraph>
        It's a pop-up that appears on your website and informs visitors of your data collection
        practices. Typically, it asks visitors for consent (and allows them to remove consent) for
        online tracking related to analytics, advertising, and site personalization.
      </HelpParagraph>
      {/* Always rendered so the toggle's aria-controls points at it; hidden while collapsed. */}
      <div
        id="consent-help-more"
        hidden={!expanded}
        className={clsx("mt-[20px] flex-col gap-[16px]", expanded ? "flex" : "hidden")}
      >
        <div>
          <HelpHeading>How does it work?</HelpHeading>
          <HelpParagraph>
            <strong className="font-semibold">Opt-in:</strong> No non-essential cookies fire unless
            a consumer accepts (or toggles on) tracking for a non-essential purpose, like
            advertising or analytics.
          </HelpParagraph>
          <HelpParagraph>
            <strong className="font-semibold">Opt-out:</strong> When a consumer toggles a consent
            type (like advertising) off, your website stops collecting and sending data to third
            parties (like ad networks) for the specified purpose of advertising.
          </HelpParagraph>
          <HelpParagraph>
            For consent to work, add the TrueVault JavaScript to your site by following the
            Developer Instructions, including the Google Tag Manager configuration. See our{" "}
            <a href={GTM_SETUP_URL} target="_blank" rel="noreferrer noopener">
              Google Tag Manager setup overview
            </a>{" "}
            for the complete implementation sequence.
          </HelpParagraph>
        </div>
        <div>
          <HelpHeading>Is a banner required?</HelpHeading>
          <HelpParagraph>
            Under US privacy laws, a consent banner is not required. However, many businesses use a
            banner in order to offer greater choices to visitors. If you do use a banner, legally
            you are required to ensure it works.
          </HelpParagraph>
        </div>
        <div>
          <HelpHeading>How do I preview my banner before going live?</HelpHeading>
          <HelpParagraph>
            Follow the technical setup in Developer Instructions, configure your banner below, then
            use Preview at the bottom of this page. To test on your staging site first, switch the
            environment to Staging.
          </HelpParagraph>
        </div>
      </div>
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls="consent-help-more"
        onClick={() => setExpanded(!expanded)}
        className="mt-[12px] cursor-pointer border-0 bg-transparent p-0 text-[14px] font-medium leading-[20px] text-primary-600 underline underline-offset-2 hover:text-primary-700"
      >
        {expanded ? "Show less" : "Show more"}
      </button>
    </HelpCard>
  );
};

const Step = ({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) => (
  <li className="grid grid-cols-[28px_minmax(0,1fr)] gap-[12px]">
    <span
      aria-hidden="true"
      className="grid h-[28px] w-[28px] place-items-center rounded-full bg-amber-50 text-[13px] font-semibold text-amber-800 ring-1 ring-inset ring-amber-200"
    >
      {number}
    </span>
    <div className="min-w-0 pt-[4px]">
      <HelpHeading>{title}</HelpHeading>
      {children}
    </div>
  </li>
);

/** Staging: how to put the staging script on a staging site and test the banner there. */
export const ConsentBannerStagingHelpCard = ({
  stagingScriptUrl,
  developerInstructionsUrl,
}: {
  stagingScriptUrl: string;
  developerInstructionsUrl: string;
}) => {
  const enqueueSnackbar = useSnackBar();

  const copyScriptUrl = async () => {
    try {
      await navigator.clipboard.writeText(stagingScriptUrl);
      enqueueSnackbar("Staging script URL copied", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Couldn't copy. Select the URL and copy it instead.", { variant: "error" });
    }
  };

  return (
    <HelpCard title="Test your banner on a staging site">
      <HelpParagraph>
        Staging keeps its own settings. Publishing here updates the banner on your staging site
        only, so you can check it before it reaches your production site.
      </HelpParagraph>
      <ol className="mb-0 mt-[20px] flex list-none flex-col gap-[16px] p-0">
        <Step number={1} title="Add the staging script to your staging site">
          <HelpParagraph>
            Follow the{" "}
            <a href={developerInstructionsUrl} target="_blank" rel="noreferrer noopener">
              Developer Instructions
            </a>
            , using this script in place of the JavaScript in Step 2 of Website Implementation.
          </HelpParagraph>
          <div className="mt-[10px] flex max-w-full items-center gap-[8px] rounded-lg border border-solid border-neutral-200 bg-neutral-100 py-[5px] pl-[12px] pr-[5px]">
            <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap bg-transparent p-0 font-mono text-[12.5px] leading-[20px] text-neutral-800">
              {stagingScriptUrl}
            </code>
            <Button
              color="tertiary"
              customSizeClassname="h-[30px] text-[13px]"
              onClick={copyScriptUrl}
            >
              <DocumentDuplicateIcon className="mr-[6px] h-[16px] w-[16px]" />
              Copy
            </Button>
          </div>
        </Step>
        <Step number={2} title="Adjust the settings below">
          <HelpParagraph>
            The first time, they start from your production settings. Publishing to staging never
            changes your live site.
          </HelpParagraph>
        </Step>
        <Step number={3} title="Publish to staging">
          <HelpParagraph>
            Use the button at the top. It can take a few minutes for changes to appear on your
            staging site.
          </HelpParagraph>
        </Step>
      </ol>
    </HelpCard>
  );
};
